// import { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { Box, Button, Typography, Chip, Paper, Alert } from "@mui/material";
// import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import AuditSidebar from "./AuditSidebar";
// import useAuditProgress from "../hooks/useAuditProgress";
// import { useFormContext } from "../context/FormContext";
// import { useProductManual } from "../context/ProductManualContext";
// import { exportSummaryPdf } from "../utils/exportSummaryPdf";
// import PdfViewer from "./PdfViewer";

// const ReportPreview = () => {
//   const { auditId } = useParams();
//   const navigate = useNavigate();
//   const { formsData } = useAuditProgress(auditId);
//   const { allFormData } = useFormContext();
//   const { data: manualData } = useProductManual();

//   const [pdfFile, setPdfFile] = useState(null);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     try {
//       const blob = exportSummaryPdf(allFormData, manualData, {
//         returnBlob: true,
//       });
//       setPdfFile(blob);
//     } catch (err) {
//       console.error("Failed to generate preview PDF:", err);
//       setError("Failed to generate the report preview. Please try again.");
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [allFormData, manualData]);

//   return (
//     <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
//       <AuditSidebar displayAuditId={auditId} formsData={formsData} />
//       <Box sx={{ flex: 1, minWidth: 0 }}>
//         <Box
//           sx={{
//             px: { xs: 2, md: 4 },
//             pt: 3,
//             pb: 4,
//             maxWidth: 1200,
//             mx: "auto",
//           }}
//         >
//           <Button
//             startIcon={<ArrowBackIcon />}
//             onClick={() => navigate(`/summary/${auditId}`)}
//             sx={{ textTransform: "none", fontWeight: 600, mb: 2 }}
//           >
//             Back to Summary
//           </Button>

//           <Paper
//             elevation={2}
//             sx={{
//               p: 2,
//               mb: 2,
//               borderRadius: 2,
//               display: "flex",
//               alignItems: "center",
//               gap: 2,
//             }}
//           >
//             <Typography variant="h6" sx={{ fontWeight: 700 }}>
//               Report Preview
//             </Typography>
//             <Chip label="DRAFT" size="small" sx={{ fontWeight: 700 }} />
//           </Paper>

//           {error && (
//             <Alert severity="error" sx={{ mb: 2 }}>
//               {error}
//             </Alert>
//           )}

//           <PdfViewer file={pdfFile} />
//         </Box>
//       </Box>
//     </Box>
//   );
// };

// export default ReportPreview;

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from './firebase.config';
import { Box, Button, Typography, Chip, Paper, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AuditSidebar from './AuditSidebar';
import useAuditProgress from '../hooks/useAuditProgress';
import { useFormContext } from '../context/FormContext';
import { useProductManual } from '../context/ProductManualContext';
import { exportSummaryPdf } from '../utils/exportSummaryPdf';
import PdfViewer from './PdfViewer';

const STATUS_COLORS = {
  draft:     'default',
  submitted: 'warning',
  published: 'success',
};

const ReportPreview = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { formsData } = useAuditProgress(auditId);
  const { allFormData } = useFormContext();
  const { data: manualData } = useProductManual();

  const [status, setStatus] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [error, setError] = useState('');

  // Fetch the audit's current status, same as SummaryTabs does.
  useEffect(() => {
    if (!auditId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, 'auditReports', auditId));
        if (snap.exists()) {
          const raw = snap.data().status || 'draft';
          setStatus(raw === 'in_progress' ? 'draft' : raw);
        }
      } catch (err) {
        console.error('Failed to fetch audit status:', err);
      }
    })();
  }, [auditId]);

  useEffect(() => {
    if (status === null) return; // wait until status is loaded
    try {
      const blob = exportSummaryPdf(allFormData, manualData, {
        returnBlob: true,
        status,
      });
      setPdfFile(blob);
    } catch (err) {
      console.error('Failed to generate preview PDF:', err);
      setError('Failed to generate the report preview. Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFormData, manualData, status]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AuditSidebar displayAuditId={auditId} formsData={formsData} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 4, maxWidth: 1200, mx: 'auto' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/summary/${auditId}`)}
            sx={{ textTransform: 'none', fontWeight: 600, mb: 2 }}
          >
            Back to Summary
          </Button>

          <Paper
            elevation={2}
            sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Report Preview
            </Typography>
            {status && (
              <Chip
                label={status.toUpperCase()}
                color={STATUS_COLORS[status] || 'default'}
                size="small"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <PdfViewer file={pdfFile} />
        </Box>
      </Box>
    </Box>
  );
};

export default ReportPreview;